/**
 * OAuth Popup Handler
 * Opens OAuth flow in a popup window and handles the callback
 */

import { getApiUrl } from "./env";

interface OAuthPopupOptions {
	url: string;
	title?: string;
	width?: number;
	height?: number;
}

export const openOAuthPopup = ({
	url,
	title = "OAuth Login",
	width = 500,
	height = 600,
}: OAuthPopupOptions): Promise<{ token: string; user: any }> => {
	return new Promise((resolve, reject) => {
		const generateChannelId = () => {
			try {
				if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
					return crypto.randomUUID();
				}
			} catch (error) {
				console.warn("[OAuthPopup] crypto.randomUUID() unavailable:", error);
			}
			return `oauth-${Date.now()}-${Math.random().toString(36).slice(2)}`;
		};

		const appendChannelIdToUrl = (targetUrl: string, channelId: string) => {
			try {
				const authUrl = new URL(targetUrl);
				const frontendRedirect = authUrl.searchParams.get("frontend_redirect");
				if (frontendRedirect) {
					const redirectUrl = new URL(frontendRedirect);
					redirectUrl.searchParams.set("channel_id", channelId);
					redirectUrl.searchParams.set("popup", "1");
					authUrl.searchParams.set("frontend_redirect", redirectUrl.toString());
					console.log(
						"[OAuthPopup] Added channel_id to frontend redirect:",
						redirectUrl.toString(),
					);
					return authUrl.toString();
				}
			} catch (error) {
				console.warn("[OAuthPopup] Failed to append channel_id to URL:", error);
			}
			return targetUrl;
		};

		const originalToken = (() => {
			try {
				return localStorage.getItem("access_token");
			} catch (error) {
				console.warn(
					"[OAuthPopup] Unable to read existing access token:",
					error,
				);
				return null;
			}
		})();

		const channelId = generateChannelId();
		const channelName = `oauth-popup-${channelId}`;
		const storageKey = `oauth_result_${channelId}`;
		const modifiedUrl = appendChannelIdToUrl(url, channelId);
		const supportsBroadcastChannel =
			typeof window !== "undefined" &&
			typeof (window as any).BroadcastChannel === "function";

		let broadcastChannel: BroadcastChannel | null = null;
		let storageListener: ((event: StorageEvent) => void) | null = null;
		let cleanupTimeout: ReturnType<typeof setTimeout> | null = null;
		let checkPopup: ReturnType<typeof setInterval> | null = null;
		let closureCheckTimeout: ReturnType<typeof setTimeout> | null = null;
		let handleMessage: ((event: MessageEvent) => void) | null = null;
		let popupReturnedToApp = false;

		const markPopupReturned = () => {
			if (!popupReturnedToApp) {
				popupReturnedToApp = true;
				console.log("[OAuthPopup] Popup returned to app origin");
			}
		};

		console.log("[OAuthPopup] Opening popup for URL:", url);
		console.log("[OAuthPopup] Current window location:", window.location.href);
		console.log("[OAuthPopup] Using channel ID:", channelId);

		// Calculate center position
		const left = window.screen.width / 2 - width / 2;
		const top = window.screen.height / 2 - height / 2;

		// Open popup window with proper features
		console.log("[OAuthPopup] Calling window.open...");

		// Use blank target first to ensure it opens in a new window
		const popup = window.open(
			"",
			title,
			`width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes,location=yes,status=yes`,
		);

		console.log("[OAuthPopup] window.open returned:", popup);
		console.log("[OAuthPopup] Popup is null?", popup === null);
		console.log("[OAuthPopup] Popup is undefined?", popup === undefined);
		console.log(
			"[OAuthPopup] Main window location after open:",
			window.location.href,
		);

		if (!popup) {
			console.error("[OAuthPopup] Popup was blocked or failed to open");
			reject(new Error("Popup blocked. Please allow popups for this site."));
			return;
		}

		console.log(
			"[OAuthPopup] Popup opened successfully, now navigating to OAuth URL...",
		);

		// Navigate the popup to the OAuth URL
		try {
			// Clear any stale storage results for this channel
			localStorage.removeItem(storageKey);
		} catch (error) {
			console.warn(
				"[OAuthPopup] Failed to clear previous storage result:",
				error,
			);
		}

		popup.location.href = modifiedUrl;

		// Track if we've received a message to avoid race conditions
		let messageReceived = false;

		let processStoredResult: () => boolean;
		let resolveFromStoredToken: () => boolean;

		const cleanup = () => {
			if (checkPopup) {
				clearInterval(checkPopup);
				checkPopup = null;
			}
			if (handleMessage) {
				window.removeEventListener("message", handleMessage);
				handleMessage = null;
			}
			if (broadcastChannel) {
				broadcastChannel.onmessage = null;
				broadcastChannel.close();
			}
			if (storageListener) {
				window.removeEventListener("storage", storageListener);
			}
			if (cleanupTimeout) {
				clearTimeout(cleanupTimeout);
				cleanupTimeout = null;
			}
			if (closureCheckTimeout) {
				clearTimeout(closureCheckTimeout);
				closureCheckTimeout = null;
			}
		};

		const resolveSuccess = (data: { token: string; user: any }) => {
			if (messageReceived) {
				console.log(
					"[OAuthPopup] Success already processed, ignoring duplicate",
				);
				return;
			}
			console.log("[OAuthPopup] Resolving OAuth success via auxiliary channel");
			messageReceived = true;
			cleanup();
			try {
				localStorage.removeItem(storageKey);
			} catch (storageError) {
				console.warn(
					"[OAuthPopup] Failed to clear storage key after success:",
					storageError,
				);
			}
			try {
				if (popup && !popup.closed) {
					popup.close();
				}
			} catch (error) {
				console.warn(
					"[OAuthPopup] Unable to close popup after success:",
					error,
				);
			}
			resolve(data);
		};

		const rejectWithError = (error: Error) => {
			if (messageReceived) {
				return;
			}
			console.log("[OAuthPopup] Rejecting OAuth flow:", error.message);
			messageReceived = true;
			cleanup();
			try {
				localStorage.removeItem(storageKey);
			} catch (storageError) {
				console.warn(
					"[OAuthPopup] Failed to clear storage key after error:",
					storageError,
				);
			}
			try {
				if (popup && !popup.closed) {
					popup.close();
				}
			} catch (closeError) {
				console.warn(
					"[OAuthPopup] Unable to close popup after error:",
					closeError,
				);
			}
			reject(error);
		};

		resolveFromStoredToken = () => {
			try {
				const currentToken = localStorage.getItem("access_token");
				if (currentToken && currentToken !== originalToken) {
					console.log(
						"[OAuthPopup] Detected new access token in storage, resolving...",
					);
					markPopupReturned();
					resolveSuccess({
						token: currentToken,
						user: null,
					});
					return true;
				}
			} catch (error) {
				console.warn(
					"[OAuthPopup] Failed to read access token from storage:",
					error,
				);
			}
			return false;
		};

		processStoredResult = () => {
			try {
				const raw = localStorage.getItem(storageKey);
				if (!raw) {
					return resolveFromStoredToken();
				}
				const data = JSON.parse(raw);
				console.log("[OAuthPopup] Found stored OAuth result:", data?.type);
				if (data.type === "oauth-success") {
					markPopupReturned();
					resolveSuccess({
						token: data.token,
						user: data.user,
					});
					return true;
				}
				if (data.type === "oauth-error") {
					markPopupReturned();
					rejectWithError(new Error(data.error || "Authentication failed"));
					return true;
				}
			} catch (error) {
				console.warn("[OAuthPopup] Failed to process stored result:", error);
			}
			return resolveFromStoredToken();
		};

		if (supportsBroadcastChannel) {
			console.log(
				"[OAuthPopup] Setting up BroadcastChannel listener:",
				channelName,
			);
			const channel = new (window as any).BroadcastChannel(
				channelName,
			) as BroadcastChannel;
			broadcastChannel = channel;
			channel.onmessage = (event: MessageEvent) => {
				console.log(
					"[OAuthPopup] BroadcastChannel message received:",
					event.data,
				);
				if (!event.data || !event.data.type) {
					return;
				}
				if (event.data.type === "oauth-success") {
					markPopupReturned();
					resolveSuccess({
						token: event.data.token,
						user: event.data.user,
					});
				} else if (event.data.type === "oauth-error") {
					markPopupReturned();
					rejectWithError(
						new Error(event.data.error || "Authentication failed"),
					);
				}
			};
		} else {
			console.log(
				"[OAuthPopup] BroadcastChannel not available, using storage fallback:",
				storageKey,
			);
			storageListener = (event: StorageEvent) => {
				if (event.key === storageKey && event.newValue) {
					try {
						const data = JSON.parse(event.newValue);
						console.log("[OAuthPopup] Storage event received:", data);
						if (data.type === "oauth-success") {
							markPopupReturned();
							resolveSuccess({
								token: data.token,
								user: data.user,
							});
						} else if (data.type === "oauth-error") {
							markPopupReturned();
							rejectWithError(new Error(data.error || "Authentication failed"));
						}
					} catch (error) {
						console.warn(
							"[OAuthPopup] Failed to parse storage event data:",
							error,
						);
					} finally {
						try {
							localStorage.removeItem(storageKey);
						} catch (error) {
							console.warn(
								"[OAuthPopup] Failed to remove storage key during cleanup:",
								error,
							);
						}
					}
					return;
				}
				if (
					event.key === "access_token" &&
					event.newValue &&
					event.newValue !== originalToken
				) {
					console.log("[OAuthPopup] Storage event detected new access token");
					markPopupReturned();
					resolveSuccess({
						token: event.newValue,
						user: null,
					});
					try {
						localStorage.removeItem(storageKey);
					} catch (error) {
						console.warn(
							"[OAuthPopup] Failed to remove storage key during cleanup:",
							error,
						);
					}
					return;
				}
			};
			window.addEventListener("storage", storageListener);
		}

		// Poll for popup closure or message
		checkPopup = setInterval(() => {
			try {
				try {
					if (
						popup.location?.origin &&
						popup.location.origin === window.location.origin
					) {
						markPopupReturned();
					}
				} catch (originError) {
					// Expected while popup is on provider domain or COOP is enforced
				}

				// Try to check if popup is closed, but ignore COOP errors
				if (popup.closed) {
					console.log(
						"[OAuthPopup] Popup closed detected, messageReceived:",
						messageReceived,
					);
					if (!messageReceived) {
						if (!popupReturnedToApp) {
							console.log(
								"[OAuthPopup] Popup appears closed before returning to our origin; waiting to avoid false cancellation",
							);
							return;
						}
						console.log(
							"[OAuthPopup] No message received yet, checking stored results...",
						);
						if (processStoredResult()) {
							return;
						}
						if (!closureCheckTimeout) {
							console.log(
								"[OAuthPopup] Starting closure timeout (5 seconds)...",
							);
							closureCheckTimeout = setTimeout(() => {
								closureCheckTimeout = null;
								if (messageReceived) {
									console.log(
										"[OAuthPopup] Message received during timeout, ignoring cancellation",
									);
									return;
								}
								if (processStoredResult()) {
									console.log(
										"[OAuthPopup] Found stored result during timeout, processing...",
									);
									return;
								}
								console.log(
									"[OAuthPopup] No success message or stored result found, treating as cancelled",
								);
								rejectWithError(new Error("Authentication cancelled"));
							}, 5000); // Increased timeout to 5 seconds to allow more time for message processing
						}
					}
				}
			} catch (error) {
				// Cross-origin error or COOP - popup is still on OAuth provider's domain
				// This is expected and normal, just continue polling
			}
		}, 500);

		// Listen for message from popup
		handleMessage = (event: MessageEvent) => {
			console.log("[OAuthPopup] Message received!");
			console.log("[OAuthPopup] Event origin:", event.origin);
			console.log("[OAuthPopup] Event data:", event.data);
			console.log("[OAuthPopup] Event data type:", event.data?.type);

			// Verify origin for security
			// The message should come from the same origin as our app (the popup callback page)
			const allowedOrigins = [window.location.origin];

			// Also allow API base URL origin in case backend serves the callback
			const apiBaseUrl = getApiUrl();
			try {
				const apiOrigin = new URL(apiBaseUrl).origin;
				if (apiOrigin !== window.location.origin) {
					allowedOrigins.push(apiOrigin);
				}
			} catch (e) {
				console.warn("[OAuthPopup] Could not parse API base URL:", apiBaseUrl);
			}

			console.log(
				"[OAuthPopup] Window location origin:",
				window.location.origin,
			);
			console.log("[OAuthPopup] API base URL:", apiBaseUrl);
			console.log("[OAuthPopup] Allowed origins:", allowedOrigins);

			if (!allowedOrigins.includes(event.origin)) {
				console.warn(
					"[OAuthPopup] Message from unauthorized origin:",
					event.origin,
				);
				console.warn("[OAuthPopup] Expected one of:", allowedOrigins);
				return;
			}

			if (event.data.type === "oauth-success") {
				markPopupReturned();
				console.log(
					"[OAuthPopup] OAuth success message received, resolving...",
				);
				resolveSuccess({
					token: event.data.token,
					user: event.data.user,
				});
			} else if (event.data.type === "oauth-error") {
				markPopupReturned();
				console.log("[OAuthPopup] OAuth error message received, rejecting...");
				rejectWithError(new Error(event.data.error || "Authentication failed"));
			} else {
				console.log("[OAuthPopup] Unknown message type, ignoring...");
			}
		};

		console.log("[OAuthPopup] Setting up message listener...");
		if (handleMessage) {
			window.addEventListener("message", handleMessage);
		}

		// Timeout after 5 minutes
		cleanupTimeout = setTimeout(() => {
			console.log("[OAuthPopup] OAuth flow timed out");
			cleanup();
			if (!popup.closed) {
				popup.close();
			}
			reject(new Error("Authentication timeout"));
		}, 5 * 60 * 1000);
	});
};
